import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { PurchaseOrder, POInsights } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Calendar, Loader2 } from 'lucide-react';
import { usePODetailFormatters } from './usePODetailFormatters';
import { getStatusBadgeClass } from './PODetailHelpers';
import { PO_HEADER_STATUS_LABELS } from './poHeaderStatus';

/** R-343: the header status by its own label — PurchaseOrder.STATUS_CHOICES.
 *
 * CR-7412: the map moved to `poHeaderStatus.ts`. It was one of FOUR copies, and the
 * fourth was a hardcoded ternary in the headers grid that had never said anything but
 * Open or Closed — so the three-state ruling was delivered on three surfaces and silently
 * not on the one users read. Local copies of a vocabulary is how that happens.
 */
const PO_STATUS_LABELS = PO_HEADER_STATUS_LABELS;

interface PODetailOverviewTabProps {
  po: PurchaseOrder;
  /** Customer review 11 Sep (item 25) / CR-7304: OTIF %, NCRs and buyers for the order. */
  insights: POInsights | null;
  insightsState: 'loading' | 'ready' | 'error';
  /** Buyer identity is customer-side (CR-4716); suppliers do not see the Buyer row. */
  isSupplierUser: boolean;
}

/** A figure card whose value comes from the order's insights. */
function MetricCard({ title, state, value, detail, tone, tooltip }: {
  title: string;
  state: 'loading' | 'ready' | 'error';
  value: ReactNode;
  detail?: ReactNode;
  tone?: 'good' | 'bad';
  tooltip?: string;
}) {
  const toneClass = tone === 'good' ? 'text-green-700' : tone === 'bad' ? 'text-red-600' : '';
  return (
    <Card title={tooltip}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {state === 'loading' ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : state === 'error' ? (
          <div className="text-sm text-muted-foreground">Unavailable</div>
        ) : (
          <>
            <div className={`text-2xl font-bold ${toneClass}`}>{value}</div>
            {detail && <div className="text-xs text-muted-foreground mt-1">{detail}</div>}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * The Overview panel of the Purchase Order Details modal.
 *
 * Note that the Customer Information card lives here and only here, which is
 * why the modal header has to name the parties itself (CR-7258) -- this panel
 * is not on screen once the user changes tab.
 *
 * Customer review 11 Sep (item 25): (a) the "Company" row under Customer
 * repeated the customer site word for word and is gone; (b) the buyers on the
 * order's lines are shown, since "Created By" is usually "System Import";
 * (c) OTIF % for the order sits next to Status, from the platform's own OTIF
 * engine (see backend po_header_insights.py). CR-7304: no PPM/K rate at PO
 * grain — the NCRs raised against the order and their total reject qty
 * instead, each opening the Quality register filtered to this order.
 */
export function PODetailOverviewTab({ po, insights, insightsState, isSupplierUser }: PODetailOverviewTabProps) {
  const { formatCurrency, formatDate } = usePODetailFormatters();
  const otif = insights?.otif;
  const ncrs = insights?.ncrs;
  const otifTone = otif?.pct == null || otif.target == null ? undefined : otif.pct >= otif.target ? 'good' : 'bad';
  // CR-7304: the Quality register filtered to exactly the NCRs counted here
  // (backend NCRFilterSet.filter_po applies the same rule).
  const ncrHref = `/quality?po=${po.id}&po_number=${encodeURIComponent(po.po_number)}`;
  const rejectQty = ncrs ? parseFloat(ncrs.reject_qty).toLocaleString() : '0';

  return (
    <>
{/* Header Info */}
<div className="grid grid-cols-1 md:grid-cols-5 gap-4">
  <Card>
    <CardHeader className="pb-3">
      <CardTitle className="text-sm font-medium text-muted-foreground">PO Number</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{po.po_number}</div>
    </CardContent>
  </Card>

  <Card>
    <CardHeader className="pb-3">
      <CardTitle className="text-sm font-medium text-muted-foreground">Status</CardTitle>
    </CardHeader>
    <CardContent>
      {/* R-343: every PurchaseOrder status by its own label and colour — the
          old Closed/Open ternary printed "Open" for five of the six. */}
      {/* CR-936 rework: header_status follows the lines, as the grid does. */}
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(po.header_status ?? po.status)}`}>
        {PO_STATUS_LABELS[po.header_status ?? po.status] || (po.header_status ?? po.status)}
      </span>
    </CardContent>
  </Card>

  <MetricCard
    title="OTIF %"
    state={insightsState}
    value={otif?.pct == null ? 'N/A' : `${otif.pct.toFixed(1)}%`}
    detail={otif ? (
      <>
        {/* CR-7304 AC2: the engine's three counts, checkable against the drill-down. */}
        <div>{otif.passed} passed · {otif.closed} closed · {otif.arrears} in arrears</div>
        {otif.closed + otif.arrears === 0 && <div>No line due or delivered yet</div>}
        {/* CR-7304 AC4: otif_engine.population leaves held lines out. */}
        {otif.on_hold > 0 && (
          <div className="text-amber-700">
            {otif.on_hold} line{otif.on_hold === 1 ? '' : 's'} on hold — excluded while held
          </div>
        )}
      </>
    )
      // Schedule-grain customers: delivery is scored per schedule line, so
      // the PO-line figure is withheld (backend po_header_insights._otif).
      : insights ? 'Scored per schedule line — see Dashboard › Scheduled Dashboards' : undefined}
    tone={otifTone}
    tooltip={otif
      ? `Passed ÷ (closed + in arrears), from the OTIF engine — the Supplier Performance formula. Lines on hold are excluded while held.${otif.target != null ? ` Target ${otif.target}%.` : ''}`
      : undefined}
  />

  {/* CR-7304 AC5/AC6: NCR count + total reject qty, not a PPM rate. Same rows
      as the Lines tab's per-line NCR badge; NCRs naming the order but no line
      of it are called out so the two always reconcile. */}
  <MetricCard
    title="NCRs raised against this PO"
    state={insightsState}
    value={ncrs && ncrs.ncr_count > 0
      ? <Link to={ncrHref} className="text-primary hover:underline" title="Open these NCRs in Quality">{ncrs.ncr_count.toLocaleString()}</Link>
      : '0'}
    detail={ncrs ? (
      <>
        <div>
          Total reject qty:{' '}
          {ncrs.ncr_count > 0
            ? <Link to={ncrHref} className="text-primary hover:underline font-medium" title="Open these NCRs in Quality">{rejectQty}</Link>
            : <span className="font-medium">0</span>}
        </div>
        {ncrs.unlinked_ncr_count > 0 && (
          <div>{ncrs.unlinked_ncr_count} not linked to a line</div>
        )}
      </>
    ) : undefined}
    tone={ncrs && ncrs.ncr_count > 0 ? 'bad' : undefined}
    tooltip="Deleted, draft and internal NCRs are not counted. No PPM rate is shown for a single order — see Supplier Performance for the supplier's PPM."
  />

  <Card>
    <CardHeader className="pb-3">
      <CardTitle className="text-sm font-medium text-muted-foreground">Total Value</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{formatCurrency(po.total_value, po.currency, 2)}</div>
    </CardContent>
  </Card>
</div>

{/* Company Information */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
  <Card>
    <CardHeader>
      <CardTitle>Customer Information</CardTitle>
    </CardHeader>
    <CardContent className="space-y-2">
      <div>
        <label className="text-xs font-medium text-muted-foreground">Customer</label>
        <div className="font-medium">{po.customer_name || 'N/A'}</div>
      </div>
      {po.delivery_warehouse_details && (
        <div>
          <label className="text-xs font-medium text-muted-foreground">Warehouse</label>
          <div className="font-medium">{po.delivery_warehouse_details.name}</div>
        </div>
      )}
      {/* review item 25b. Customer-side only (CR-4716); the API also withholds it. */}
      {!isSupplierUser && (
        <div>
          <label className="text-xs font-medium text-muted-foreground">Buyer</label>
          {insightsState === 'loading' ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : insights?.buyers && insights.buyers.length > 0 ? (
            insights.buyers.map((b) => (
              <div key={`${b.code}|${b.name}`} className="font-medium">
                {b.name || b.code}{b.name && b.code ? ` (${b.code})` : ''}
              </div>
            ))
          ) : (
            <div className="text-sm text-muted-foreground">
              {insightsState === 'error' ? 'Unavailable' : 'No buyer recorded on this order’s lines'}
            </div>
          )}
        </div>
      )}
      <div>
        <label className="text-xs font-medium text-muted-foreground">Created By</label>
        <div className="font-medium">{po.created_by_name || 'N/A'}</div>
      </div>
    </CardContent>
  </Card>

  <Card>
    <CardHeader>
      <CardTitle>Supplier Information</CardTitle>
    </CardHeader>
    <CardContent className="space-y-2">
      <div>
        <label className="text-xs font-medium text-muted-foreground">Company</label>
        <div className="font-medium">{po.supplier_name}</div>
      </div>
      {po.supplier_group_name && (
        <div>
          <label className="text-xs font-medium text-muted-foreground">Supplier Group</label>
          <div className="font-medium">{po.supplier_group_name}</div>
        </div>
      )}
    </CardContent>
  </Card>
</div>

{/* Dates and Terms */}
<Card>
  <CardHeader>
    <CardTitle>Dates & Terms</CardTitle>
  </CardHeader>
  <CardContent>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div>
        <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          Issue Date
        </label>
        <div className="font-medium mt-1">{formatDate(po.issue_date)}</div>
      </div>
      <div>
        <label className="text-xs font-medium text-muted-foreground">Currency</label>
        <div className="font-medium mt-1">{po.currency}</div>
      </div>
    </div>
    {po.payment_terms && (
      <div className="mt-4">
        <label className="text-xs font-medium text-muted-foreground">Payment Terms</label>
        <div className="font-medium mt-1">{po.payment_terms}</div>
      </div>
    )}
    {/* CR-7363: the order's remarks as held in the ERP. */}
    {po.erp_remarks && (
      <div className="mt-4">
        <label className="text-xs font-medium text-muted-foreground">Remarks</label>
        <div className="text-sm mt-1 p-3 bg-muted rounded-lg whitespace-pre-wrap">{po.erp_remarks}</div>
      </div>
    )}
    {po.notes && (
      <div className="mt-4">
        <label className="text-xs font-medium text-muted-foreground">Notes</label>
        <div className="text-sm mt-1 p-3 bg-muted rounded-lg">{po.notes}</div>
      </div>
    )}
  </CardContent>
</Card>
    </>
  );
}
